import { WorkspaceChatPlatformCaptionCard } from './workspace-chat-platform-caption-card'
import { type WorkspaceChatNewMessage } from '@backend/routes/stream/workspace-chat'
import { type PlatformType } from '@backend/lib/db/schema/platform'
import { type WorkspaceCaptions } from '@backend/lib/db/schema/workspace'
import {
  CircleCheck,
  Clock,
  ExternalLink,
  Eye,
  FileText,
  Github,
  Globe,
  Redo2,
  Send,
  Undo2,
} from 'lucide-react'
import { Button } from '@/components/ui/shadcn/button'
import { MarkdownContent } from '@/components/ui/markdown'
import { getPlatformColor } from '@/lib/calendar/post-adapter'
import { getPlatformDisplayName } from '@/lib/calendar/types'
import { Link } from 'react-router'

interface Props {
  message: WorkspaceChatNewMessage
  currentCaptions: WorkspaceCaptions
  onApplyCaption: (caption: string, platform: PlatformType, title?: string) => void
  isStreaming?: boolean
  /** Undo/redo direction per updateCaptions toolCallId; absent = toggle unavailable (e.g. after reload). */
  captionEditVersions?: Record<string, 'new' | 'old'>
  onToggleCaptionEdit?: (toolCallId: string) => void
  /** Answers per askConfirmation toolCallId — disables the buttons once clicked. */
  confirmationAnswers?: Record<string, 'confirmed' | 'declined'>
  onConfirmationAnswer?: (toolCallId: string, confirmed: boolean) => void
  /** Undo/redo direction per editDesign toolCallId (session-only, like caption edits). */
  designEditVersions?: Record<string, 'new' | 'old'>
  onToggleDesignEdit?: (toolCallId: string) => void
}

const CAPTION_KEYS = ['instagram', 'linkedin', 'tiktok', 'youtube', 'facebook', 'meta-ads'] as const

export function WorkspaceChatMessageItem({
  message,
  currentCaptions,
  onApplyCaption,
  isStreaming = false,
  captionEditVersions = {},
  onToggleCaptionEdit,
  confirmationAnswers = {},
  onConfirmationAnswer,
  designEditVersions = {},
  onToggleDesignEdit,
}: Props) {
  if (!message || !message.parts || !Array.isArray(message.parts)) {
    console.warn('Invalid message structure:', message)
    return null
  }

  if (message.role === 'user') {
    return (
      <div className="mb-4 flex justify-end">
        <div className="bg-primary max-w-[420px] rounded-lg px-3.5 py-2.5 text-white">
          {message.parts.map((part, i) => {
            if (part.type === 'text') {
              const text = typeof part.text === 'string' ? part.text : ''
              if (!text) return null
              return (
                <div key={`${message.id}-${part.type}-${i}`} className="text-sm">
                  {text}
                </div>
              )
            }
            return null
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="mb-4 flex justify-start">
      <div className="max-w-[420px] p-2">
        {/* Regular text parts */}
        {message.parts.map((part, i) => {
          if (part.type === 'text') {
            return (
              <div key={`${message.id}-${part.type}-${i}`} className="mb-2 text-sm">
                <MarkdownContent isStreaming={isStreaming}>{part.text}</MarkdownContent>
              </div>
            )
          }

          if (part.type === 'tool-getPlatformBestPractice') {
            switch (part.state) {
              case 'input-streaming':
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="bg-card rounded-md p-2">
                      <div className="text-primary text-sm">Looking up best practices...</div>
                    </div>
                  </div>
                )
              case 'input-available':
              case 'output-available':
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="bg-card rounded-md p-2">
                      <div className="text-primary text-sm">Best practices found!</div>
                    </div>
                  </div>
                )
              case 'output-error':
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="border-destructive/50 bg-destructive/10 rounded-md border p-2">
                      <div className="text-destructive text-sm">
                        Something went wrong. Please try again.
                      </div>
                    </div>
                  </div>
                )
            }
          }

          if (part.type === 'tool-generatePost') {
            switch (part.state) {
              case 'input-streaming':
              case 'input-available':
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="bg-card rounded-md p-3">
                      <div className="flex items-center gap-2">
                        <div className="border-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                        <div className="text-foreground text-sm">
                          Writing your post — watch the captions come in...
                        </div>
                      </div>
                    </div>
                  </div>
                )
              case 'output-available':
                if (part.output?.success === false) {
                  return (
                    <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                      <div className="border-destructive/50 bg-destructive/10 rounded-md border p-2">
                        <div className="text-destructive text-sm">
                          Post generation failed. Please try again.
                        </div>
                      </div>
                    </div>
                  )
                }
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="bg-card rounded-md p-3">
                      <div className="flex items-center gap-2">
                        <CircleCheck className="h-4 w-4 text-green-500" />
                        <span className="text-foreground text-sm">
                          Post created{part.output?.title ? `: ${part.output.title}` : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                )
              case 'output-error':
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="border-destructive/50 bg-destructive/10 rounded-md border p-2">
                      <div className="text-destructive text-sm">
                        Something went wrong. Please try again.
                      </div>
                    </div>
                  </div>
                )
            }
          }

          if (part.type === 'tool-updateCaptions') {
            switch (part.state) {
              case 'input-streaming':
              case 'input-available':
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="bg-card rounded-md p-3">
                      <div className="flex items-center gap-2">
                        <div className="border-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                        <div className="text-foreground text-sm">Updating your caption...</div>
                      </div>
                    </div>
                  </div>
                )
              case 'output-available': {
                const input = (part.input ?? {}) as Partial<Record<string, string>>
                const platforms = CAPTION_KEYS.filter((p) => typeof input[p] === 'string')
                const label =
                  platforms.length > 0
                    ? platforms.map((p) => getPlatformDisplayName(p)).join(', ')
                    : 'caption'
                const version = captionEditVersions[part.toolCallId]
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="bg-card rounded-md p-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-2">
                          <CircleCheck className="h-4 w-4 shrink-0 text-green-500" />
                          <span className="text-foreground truncate text-sm">
                            {version === 'old' ? `Previous ${label} caption` : `Updated ${label}`}
                          </span>
                        </div>
                        {version && onToggleCaptionEdit && (
                          <button
                            onClick={() => onToggleCaptionEdit(part.toolCallId)}
                            className="text-muted-foreground hover:text-foreground flex shrink-0 items-center gap-1 text-xs font-medium transition-colors">
                            {version === 'new' ? (
                              <>
                                <Undo2 className="h-3.5 w-3.5" />
                                Undo
                              </>
                            ) : (
                              <>
                                <Redo2 className="h-3.5 w-3.5" />
                                Redo
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )
              }
              case 'output-error':
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="border-destructive/50 bg-destructive/10 rounded-md border p-2">
                      <div className="text-destructive text-sm">
                        Something went wrong. Please try again.
                      </div>
                    </div>
                  </div>
                )
            }
          }

          // Legacy: older conversations still contain Apply-based
          // captionSuggestions cards; the tool no longer exists but persisted
          // messages should keep rendering.
          if ((part.type as string) === 'tool-captionSuggestions') {
            const legacy = part as unknown as {
              toolCallId: string
              state: string
              input?: WorkspaceCaptions & { youtubeTitle?: string }
            }
            if (legacy.state === 'input-available' || legacy.state === 'output-available') {
              return (
                <WorkspaceChatPlatformCaptionCard
                  key={`${message.id}-${legacy.toolCallId}-${i}`}
                  captions={legacy.input ?? {}}
                  currentCaptions={currentCaptions}
                  onApply={onApplyCaption}
                />
              )
            }
            return null
          }

          if (part.type === 'tool-captionRating') {
            switch (part.state) {
              case 'input-streaming':
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="bg-card rounded-md p-2">
                      <div className="text-primary text-sm">Analyzing caption...</div>
                    </div>
                  </div>
                )
              case 'input-available':
              case 'output-available':
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="bg-card rounded-md p-3">
                      <div className="flex items-center gap-2">
                        <CircleCheck className="h-4 w-4 text-green-500" />
                        <div className="text-foreground text-sm">Post rating completed</div>
                      </div>
                    </div>
                  </div>
                )
              case 'output-error':
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="border-destructive/50 bg-destructive/10 rounded-md border p-2">
                      <div className="text-destructive text-sm">
                        Something went wrong. Please try again.
                      </div>
                    </div>
                  </div>
                )
            }
          }

          if (part.type === 'tool-generateDesign') {
            switch (part.state) {
              case 'input-streaming':
              case 'input-available':
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="bg-card rounded-md p-3">
                      <div className="flex items-center gap-2">
                        <div className="border-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                        <div className="text-foreground text-sm">Designing graphic...</div>
                      </div>
                    </div>
                  </div>
                )
              case 'output-available':
                if (part.output?.success === false) {
                  return (
                    <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                      <div className="border-destructive/50 bg-destructive/10 rounded-md border p-2">
                        <div className="text-destructive text-sm">
                          Failed to generate design. Please try again.
                        </div>
                      </div>
                    </div>
                  )
                }
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="bg-card rounded-md p-3">
                      <div className="flex items-center gap-2">
                        <CircleCheck className="h-4 w-4 text-green-500" />
                        <span className="text-foreground text-sm">Design generated</span>
                      </div>
                    </div>
                  </div>
                )
              case 'output-error':
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="border-destructive/50 bg-destructive/10 rounded-md border p-2">
                      <div className="text-destructive text-sm">
                        Something went wrong. Please try again.
                      </div>
                    </div>
                  </div>
                )
            }
          }

          if (part.type === 'tool-schedulePost') {
            switch (part.state) {
              case 'input-streaming':
              case 'input-available':
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="bg-card rounded-md p-3">
                      <div className="flex items-center gap-2">
                        <div className="border-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                        <div className="text-foreground text-sm">Scheduling post...</div>
                      </div>
                    </div>
                  </div>
                )
              case 'output-available': {
                if (part.output?.success === false) {
                  return (
                    <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                      <div className="border-destructive/50 bg-destructive/10 rounded-md border p-2">
                        <div className="text-destructive text-sm">
                          Failed to schedule post: {part.output.error}
                        </div>
                      </div>
                    </div>
                  )
                }

                const scheduledDate = new Date(part.output.scheduledAt)
                const platform = part.output.platform as PlatformType
                const dateStr = scheduledDate.toLocaleDateString(undefined, {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                })
                const timeStr = scheduledDate.toLocaleTimeString(undefined, {
                  hour: 'numeric',
                  minute: '2-digit',
                })
                const dateParam = scheduledDate.toISOString().split('T')[0]

                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="bg-card overflow-hidden rounded-md">
                      <div className="flex items-center gap-2 p-3">
                        <CircleCheck className="h-4 w-4 shrink-0 text-green-500" />
                        <span className="text-foreground text-sm font-medium">Post scheduled</span>
                      </div>
                      <div className="border-border/60 border-t px-3 py-2.5">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div
                              className="rounded-md px-2.5 py-0.5 text-xs font-medium text-white"
                              style={{ backgroundColor: getPlatformColor(platform) }}>
                              {getPlatformDisplayName(platform)}
                            </div>
                            <div className="text-muted-foreground flex items-center gap-1 text-xs">
                              <Clock className="h-3 w-3" />
                              {dateStr}, {timeStr}
                            </div>
                          </div>
                        </div>
                      </div>
                      <Link
                        to={`/posts?date=${dateParam}`}
                        className="text-primary flex items-center justify-center gap-1.5 px-3 pb-2.5 pt-1 text-xs font-medium hover:underline">
                        <ExternalLink className="h-3 w-3" />
                        View in calendar
                      </Link>
                    </div>
                  </div>
                )
              }
              case 'output-error':
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="border-destructive/50 bg-destructive/10 rounded-md border p-2">
                      <div className="text-destructive text-sm">
                        Something went wrong. Please try again.
                      </div>
                    </div>
                  </div>
                )
            }
          }

          if (part.type === 'tool-publishPost') {
            switch (part.state) {
              case 'input-streaming':
              case 'input-available':
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="bg-card rounded-md p-3">
                      <div className="flex items-center gap-2">
                        <div className="border-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                        <div className="text-foreground text-sm">Publishing your post...</div>
                      </div>
                    </div>
                  </div>
                )
              case 'output-available': {
                if (part.output?.success === false) {
                  return (
                    <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                      <div className="border-destructive/50 bg-destructive/10 rounded-md border p-2">
                        <div className="text-destructive text-sm">
                          Failed to publish: {part.output.error}
                        </div>
                      </div>
                    </div>
                  )
                }
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="bg-card rounded-md p-3">
                      <div className="flex items-center gap-2">
                        <Send className="h-4 w-4 shrink-0 text-green-500" />
                        <span className="text-foreground text-sm font-medium">Post published</span>
                        {part.output?.link && (
                          <a
                            href={part.output.link}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary ml-auto flex items-center gap-1 text-xs font-medium hover:underline">
                            <ExternalLink className="h-3 w-3" />
                            View post
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                )
              }
              case 'output-error':
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="border-destructive/50 bg-destructive/10 rounded-md border p-2">
                      <div className="text-destructive text-sm">
                        Something went wrong. Please try again.
                      </div>
                    </div>
                  </div>
                )
            }
          }

          if (part.type === 'tool-readUrl') {
            switch (part.state) {
              case 'input-streaming':
              case 'input-available':
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="bg-card rounded-md p-2">
                      <div className="flex items-center gap-2">
                        <div className="border-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                        <div className="text-foreground text-sm">Reading the page...</div>
                      </div>
                    </div>
                  </div>
                )
              case 'output-available':
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="bg-card rounded-md p-2">
                      <div className="flex items-center gap-2">
                        <Globe className="text-muted-foreground h-4 w-4 shrink-0" />
                        <span className="text-foreground truncate text-sm">
                          {part.output?.success === false
                            ? "Couldn't read the page"
                            : `Read: ${part.output?.title || part.input?.url || 'page'}`}
                        </span>
                      </div>
                    </div>
                  </div>
                )
              case 'output-error':
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="bg-card rounded-md p-2">
                      <div className="text-muted-foreground text-sm">Couldn't read the page</div>
                    </div>
                  </div>
                )
            }
          }

          // Connector read tools (GitHub / Notion): compact "reading… / read X" chips.
          const CONNECTOR_TOOLS: Record<string, string> = {
            'tool-githubListReleases': 'releases',
            'tool-githubListCommits': 'commits',
            'tool-githubGetPullRequest': 'pull request',
            'tool-githubReadFile': 'file',
            'tool-notionRead': 'Notion',
          }
          if (CONNECTOR_TOOLS[part.type as string]) {
            const p = part as unknown as {
              type: string
              state: 'input-streaming' | 'input-available' | 'output-available' | 'output-error'
              toolCallId: string
              input?: { owner?: string; repo?: string; path?: string }
              output?: { success?: boolean; title?: string }
            }
            const isGithub = p.type.startsWith('tool-github')
            const Icon = isGithub ? Github : FileText
            const noun = CONNECTOR_TOOLS[p.type]
            const detail = isGithub
              ? [
                  p.input?.owner && p.input?.repo ? `${p.input.owner}/${p.input.repo}` : noun,
                  p.input?.path,
                ]
                  .filter(Boolean)
                  .join(' ')
              : p.output?.title || 'Notion'
            const key = `${message.id}-${p.toolCallId}-${i}`
            if (p.state === 'input-streaming' || p.state === 'input-available') {
              return (
                <div key={key} className="my-3">
                  <div className="bg-card rounded-md p-2">
                    <div className="flex items-center gap-2">
                      <div className="border-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                      <div className="text-foreground text-sm">Reading {noun}...</div>
                    </div>
                  </div>
                </div>
              )
            }
            const failed = p.state === 'output-error' || p.output?.success === false
            return (
              <div key={key} className="my-3">
                <div className="bg-card rounded-md p-2">
                  <div className="flex items-center gap-2">
                    <Icon className="text-muted-foreground h-4 w-4 shrink-0" />
                    <span className="text-foreground truncate text-sm">
                      {failed ? `Couldn't read ${noun}` : `Read: ${detail}`}
                    </span>
                  </div>
                </div>
              </div>
            )
          }

          if (part.type === 'tool-editDesign') {
            switch (part.state) {
              case 'input-streaming':
              case 'input-available':
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="bg-card rounded-md p-3">
                      <div className="flex items-center gap-2">
                        <div className="border-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                        <div className="text-foreground text-sm">Editing your design...</div>
                      </div>
                    </div>
                  </div>
                )
              case 'output-available': {
                if (part.output?.success === false) {
                  return (
                    <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                      <div className="border-destructive/50 bg-destructive/10 rounded-md border p-2">
                        <div className="text-destructive text-sm">
                          Failed to edit the design: {part.output.error}
                        </div>
                      </div>
                    </div>
                  )
                }
                const version = designEditVersions[part.toolCallId]
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="bg-card rounded-md p-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-2">
                          <CircleCheck className="h-4 w-4 shrink-0 text-green-500" />
                          <span className="text-foreground truncate text-sm">
                            {version === 'old'
                              ? 'Showing the previous design'
                              : part.output?.mode === 'redesigned'
                                ? 'Design restyled'
                                : 'Design edited'}
                          </span>
                        </div>
                        {version && onToggleDesignEdit && (
                          <button
                            onClick={() => onToggleDesignEdit(part.toolCallId)}
                            className="text-muted-foreground hover:text-foreground flex shrink-0 items-center gap-1 text-xs font-medium transition-colors">
                            {version === 'new' ? (
                              <>
                                <Undo2 className="h-3.5 w-3.5" />
                                Undo
                              </>
                            ) : (
                              <>
                                <Redo2 className="h-3.5 w-3.5" />
                                Redo
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )
              }
              case 'output-error':
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="border-destructive/50 bg-destructive/10 rounded-md border p-2">
                      <div className="text-destructive text-sm">
                        Something went wrong. Please try again.
                      </div>
                    </div>
                  </div>
                )
            }
          }

          if (part.type === 'tool-editImage') {
            switch (part.state) {
              case 'input-streaming':
              case 'input-available':
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="bg-card rounded-md p-3">
                      <div className="flex items-center gap-2">
                        <div className="border-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                        <div className="text-foreground text-sm">Working on the image...</div>
                      </div>
                    </div>
                  </div>
                )
              case 'output-available': {
                if (part.output?.success === false) {
                  return (
                    <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                      <div className="border-destructive/50 bg-destructive/10 rounded-md border p-2">
                        <div className="text-destructive text-sm">
                          Failed to update the image: {part.output.error}
                        </div>
                      </div>
                    </div>
                  )
                }
                const version = designEditVersions[part.toolCallId]
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="bg-card rounded-md p-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-2">
                          <CircleCheck className="h-4 w-4 shrink-0 text-green-500" />
                          <span className="text-foreground truncate text-sm">
                            {version === 'old'
                              ? 'Showing the previous image'
                              : part.output?.created
                                ? 'Image generated'
                                : 'Image updated'}
                          </span>
                        </div>
                        {version && onToggleDesignEdit && (
                          <button
                            onClick={() => onToggleDesignEdit(part.toolCallId)}
                            className="text-muted-foreground hover:text-foreground flex shrink-0 items-center gap-1 text-xs font-medium transition-colors">
                            {version === 'new' ? (
                              <>
                                <Undo2 className="h-3.5 w-3.5" />
                                Undo
                              </>
                            ) : (
                              <>
                                <Redo2 className="h-3.5 w-3.5" />
                                Redo
                              </>
                            )}
                          </button>
                        )}
                      </div>
                      {part.output?.imagePrompt && (
                        <div className="border-border/60 mt-2 border-t pt-2">
                          <div className="text-muted-foreground text-[10px] font-medium uppercase tracking-wide">
                            Image prompt
                          </div>
                          <div className="text-foreground/80 mt-1 max-h-32 overflow-y-auto whitespace-pre-wrap text-xs">
                            {part.output.imagePrompt}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )
              }
              case 'output-error':
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="border-destructive/50 bg-destructive/10 rounded-md border p-2">
                      <div className="text-destructive text-sm">
                        Something went wrong. Please try again.
                      </div>
                    </div>
                  </div>
                )
            }
          }

          if (part.type === 'tool-askConfirmation') {
            if (part.state === 'input-streaming') return null
            const question = part.input?.question ?? 'Should I go ahead?'
            const answer = confirmationAnswers[part.toolCallId]
            return (
              <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                <div className="border-border bg-card rounded-md border p-3">
                  <div className="text-foreground text-sm">{question}</div>
                  <div className="mt-2.5 flex items-center gap-2">
                    {answer ? (
                      <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
                        <CircleCheck className="h-3.5 w-3.5" />
                        {answer === 'confirmed' ? 'Confirmed' : 'Cancelled'}
                      </span>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          className="h-7 text-xs"
                          disabled={!onConfirmationAnswer}
                          onClick={() => onConfirmationAnswer?.(part.toolCallId, true)}>
                          {part.input?.confirmLabel || 'Confirm'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs"
                          disabled={!onConfirmationAnswer}
                          onClick={() => onConfirmationAnswer?.(part.toolCallId, false)}>
                          Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )
          }

          if (part.type === 'tool-viewMedia') {
            switch (part.state) {
              case 'input-streaming':
              case 'input-available':
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="bg-card rounded-md p-2">
                      <div className="flex items-center gap-2">
                        <div className="border-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                        <div className="text-foreground text-sm">Looking at your media...</div>
                      </div>
                    </div>
                  </div>
                )
              case 'output-available':
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="bg-card rounded-md p-2">
                      <div className="flex items-center gap-2">
                        <Eye className="text-muted-foreground h-4 w-4 shrink-0" />
                        <span className="text-foreground text-sm">
                          Viewed{' '}
                          {[
                            ...(part.input?.indexes ?? []).map((n: number) => `media [${n}]`),
                            ...(part.input?.attachmentIndexes ?? []).map(
                              (n: number) => `attachment [${n}]`,
                            ),
                          ].join(', ') || 'media'}
                        </span>
                      </div>
                    </div>
                  </div>
                )
              case 'output-error':
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="bg-card rounded-md p-2">
                      <div className="text-muted-foreground text-sm">Couldn't view the media</div>
                    </div>
                  </div>
                )
            }
          }

          if (
            part.type === 'tool-listScheduledPosts' ||
            part.type === 'tool-cancelScheduledPost' ||
            part.type === 'tool-reschedulePost'
          ) {
            const runningLabel =
              part.type === 'tool-listScheduledPosts'
                ? 'Checking your scheduled posts...'
                : part.type === 'tool-cancelScheduledPost'
                  ? 'Cancelling the scheduled post...'
                  : 'Rescheduling the post...'
            const doneLabel =
              part.type === 'tool-listScheduledPosts'
                ? 'Checked your scheduled posts'
                : part.type === 'tool-cancelScheduledPost'
                  ? 'Scheduled post cancelled'
                  : 'Post rescheduled'
            switch (part.state) {
              case 'input-streaming':
              case 'input-available':
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="bg-card rounded-md p-2">
                      <div className="flex items-center gap-2">
                        <div className="border-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                        <div className="text-foreground text-sm">{runningLabel}</div>
                      </div>
                    </div>
                  </div>
                )
              case 'output-available': {
                const failed =
                  part.type !== 'tool-listScheduledPosts' && part.output?.success === false
                if (failed) {
                  return (
                    <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                      <div className="border-destructive/50 bg-destructive/10 rounded-md border p-2">
                        <div className="text-destructive text-sm">
                          {'error' in (part.output ?? {})
                            ? (part.output as { error: string }).error
                            : 'Something went wrong.'}
                        </div>
                      </div>
                    </div>
                  )
                }
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="bg-card rounded-md p-2">
                      <div className="flex items-center gap-2">
                        <CircleCheck className="h-4 w-4 shrink-0 text-green-500" />
                        <span className="text-foreground text-sm">{doneLabel}</span>
                      </div>
                    </div>
                  </div>
                )
              }
              case 'output-error':
                return (
                  <div key={`${message.id}-${part.toolCallId}-${i}`} className="my-3">
                    <div className="border-destructive/50 bg-destructive/10 rounded-md border p-2">
                      <div className="text-destructive text-sm">
                        Something went wrong. Please try again.
                      </div>
                    </div>
                  </div>
                )
            }
          }
          return null
        })}
      </div>
    </div>
  )
}
